import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { post } from '../../../../api/baseUrl';
import { useLocation } from 'react-router-dom';
import { MedProfile, UpdatePatientMedProfilePayload } from './types';
import { omit } from 'lodash';
import { tranformTaperFormat } from '../../../../lib/transferObjects/Medication';
import { useEnsurePayloadMedSectionOrder } from './useEnsurePayloadMedSectionOrder';

interface CreatePatientMedProfilesResponse {
  medProfiles: MedProfile[];
  access_token: string;
  tenancyId: number;
  id: string;
}

export const useCreateMedProfiles = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [medProfiles, setMedProfiles] = useState<MedProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { ensurePayloadMedSectionsOrder } = useEnsurePayloadMedSectionOrder();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  const postCreatePatientMedProfiles = async (medProfile: MedProfile) => {
    if (!selectedTenancy || !access_token) return;
    setIsLoading(true);
    try {
      const trimedMedProfile = omit(medProfile, [
        'finalisedDate',
        'createdTenancyId',
        'clientClinicEpisodeId',
        'createdAt',
        'updatedBy',
        'createdBy',
        'finalisedBy',
        'caremapprId',
        'updatedAt',
        'isCurrent',
        'id',
        'hospitalVerified',
        'healthProviderVerified',
      ]);
      const normalizedMedSections = ensurePayloadMedSectionsOrder(
        trimedMedProfile?.medSections,
      );

      const createPayload: UpdatePatientMedProfilePayload = {
        access_token: access_token,
        tenancyId: selectedTenancy.tenancy_id,
        medProfile: {
          ...trimedMedProfile,
          id: null,
          medSections: normalizedMedSections.map((section, index) => {
            const { sectionText: _sectionText, ...rest } = section as any;
            return {
              ...rest,
              order: Number((section as any)?.order) || index + 1,
              medications: (Array.isArray(section.medications)
                ? section.medications
                : []
              ).map((medication, medIndex) => ({
                ...omit(medication, [
                  'createdAt',
                  'deletedAt',
                  'updatedAt',
                  'hospitalVerified',
                  'healthProviderVerified',
                  'medSectionBreak',
                  'indicationOptions',
                  'indications',
                ]),
                order: Number(medication?.order) || medIndex + 1,
                regimen: medication.regimen
                  ? {
                      ...medication.regimen,
                      taper: medication.regimen.taper
                        ? tranformTaperFormat(medication.regimen.taper)
                        : undefined,
                    }
                  : undefined,
              })),
            };
          }),
        },
      };
      const { data: result } = await post(`updatePatientMedProfile`, {
        ...createPayload,
      });
      const { data }: { data: CreatePatientMedProfilesResponse } = result;
      if (Array.isArray(data.medProfiles)) {
        setMedProfiles(data.medProfiles);
      }
      return data;
    } catch (error: any) {
      console.error('An error occurred during updatePatientMedProfile', error);
      setErrorMessage(
        error?.response?.data?.message ||
          'Unknown error in updatePatientMedProfile',
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    postCreatePatientMedProfiles,
    errorMessage,
    isLoading,
    medProfiles,
  };
};
